import React from 'react';
import { Link } from 'react-router-dom';
import { Award, BarChart3, Globe, Shield, Star, CheckCircle, ArrowRight, Zap, Target, BookOpen } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { tpiDimensions } from '../data/mock';

const TPIPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero with Certificate */}
        <section className="bg-gradient-to-b from-[#3459a4]/5 to-transparent py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-[#3459a4]/10 rounded-full px-4 py-2 mb-6">
                  <Award className="w-4 h-4 text-[#3459a4]" />
                  <span className="text-sm font-medium text-[#3459a4]">Globally Recognized</span>
                </div>
                <h1 className="heading-1 mb-4">
                  Tenses Proficiency Index
                  <span className="block text-[#3459a4]">(TPI)</span>
                </h1>
                <p className="body-large mb-6">
                  A state-of-the-art framework for assessing, improving, and certifying 
                  English language skills, combining structured assessment with modern 
                  AI evaluation techniques.
                </p>
                <p className="text-[#83928c] mb-8">
                  TPI evaluates learners across <strong className="text-[#1a1a2e]">8 core dimensions</strong> of 
                  English proficiency, producing a comprehensive score from <strong className="text-[#1a1a2e]">0 to 1200</strong>, 
                  with each dimension worth up to 150 points.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="https://app.tenses.ai" className="btn-primary">
                    Start Certification Journey
                  </a>
                  <Link to="/activities" className="btn-secondary">
                    View Activities
                  </Link>
                </div>
              </div>

              {/* Right: Certificate Preview */}
              <div className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-black/5">
                  <img 
                    src="/tenses-certificate.svg" 
                    alt="Tenses Certificate" 
                    className="w-full rounded-lg"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 tpi-badge text-lg py-3 px-6">
                  <Star className="w-5 h-5" />
                  TPI Score: 945
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What is TPI */}
        <section className="section">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-center mb-4">What is the Tenses Proficiency Index?</h2>
            <p className="body-large text-center mb-8">
              TPI is a comprehensive English assessment framework designed for the modern learner.
            </p>
            
            <div className="bg-white rounded-2xl p-8 border border-black/10 mb-8">
              <p className="text-[#1a1a2e] mb-4">
                The <strong>Tenses Proficiency Index (TPI)</strong> is a proprietary framework for measuring 
                and certifying English language proficiency. Unlike traditional tests that provide a single score, 
                TPI evaluates your skills across <strong>8 distinct dimensions</strong>, giving you a detailed 
                understanding of your strengths and areas for improvement.
              </p>
              <p className="text-[#83928c]">
                Each dimension is scored out of 150 points, contributing to a maximum total score of 1200. 
                This granular approach allows for precise skill assessment and targeted learning recommendations.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#3459a4]/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#3459a4]">1200</div>
                <div className="text-sm text-[#83928c]">Maximum Score</div>
              </div>
              <div className="bg-[#3459a4]/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#3459a4]">8</div>
                <div className="text-sm text-[#83928c]">Core Dimensions</div>
              </div>
              <div className="bg-[#3459a4]/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#3459a4]">150</div>
                <div className="text-sm text-[#83928c]">Points Per Dimension</div>
              </div>
              <div className="bg-[#3459a4]/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#3459a4]">5</div>
                <div className="text-sm text-[#83928c]">Proficiency Levels</div>
              </div>
            </div>
          </div>
        </section>

        {/* What Sets TPI Apart */}
        <section className="section section-alt">
          <h2 className="heading-2 text-center mb-4">What Sets TPI Apart</h2>
          <p className="body-large text-center max-w-2xl mx-auto mb-12">
            TPI goes beyond traditional assessments with granular scoring and adaptive learning.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="card text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#3459a4]/10 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-7 h-7 text-[#3459a4]" />
              </div>
              <h3 className="font-semibold text-lg text-[#1a1a2e] mb-2">Granular Scoring</h3>
              <p className="text-sm text-[#83928c]">
                A 0-1200 point scale across eight separate dimensions, 
                allowing you to track incremental improvements.
              </p>
            </div>
            <div className="card text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#3459a4]/10 flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-[#3459a4]" />
              </div>
              <h3 className="font-semibold text-lg text-[#1a1a2e] mb-2">Expanded Skill Set</h3>
              <p className="text-sm text-[#83928c]">
                Explicitly evaluates Thoroughness, Vocabulary, Grammar, Writing Skills, 
                and Attention to Detail beyond traditional tests.
              </p>
            </div>
            <div className="card text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#3459a4]/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-[#3459a4]" />
              </div>
              <h3 className="font-semibold text-lg text-[#1a1a2e] mb-2">Adaptive Learning</h3>
              <p className="text-sm text-[#83928c]">
                Dynamic feedback and targeted practice tasks create personalized 
                learning paths based on your performance.
              </p>
            </div>
          </div>
        </section>

        {/* 8 Core Dimensions */}
        <section className="section">
          <h2 className="heading-2 text-center mb-4">8 Core Dimensions</h2>
          <p className="body-large text-center max-w-2xl mx-auto mb-12">
            TPI evaluates your English proficiency across eight carefully designed dimensions, 
            each contributing 150 points to your total score.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {tpiDimensions.map((dimension, index) => (
              <div key={dimension.name} className="bg-white rounded-xl p-5 border border-black/5 hover:border-[#3459a4]/30 hover:shadow-md transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-full bg-[#3459a4] text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>
                  <h4 className="font-semibold text-[#1a1a2e]">{dimension.name}</h4>
                </div>
                <p className="text-sm text-[#83928c] mb-3">{dimension.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#3459a4] font-medium">Max: {dimension.maxScore} pts</span>
                  <div className="w-16 h-1.5 bg-[#3459a4]/20 rounded-full overflow-hidden">
                    <div className="h-full bg-[#3459a4] rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dimension Details */}
          <div className="max-w-4xl mx-auto mt-12 bg-white rounded-2xl p-8 border border-black/10">
            <h3 className="font-semibold text-lg text-[#1a1a2e] mb-6">Dimension Details</h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">1. Fluency</h4>
                  <p className="text-sm text-[#83928c]">Speech flow, coherence, and ease of articulation. Measures how smoothly you communicate without hesitation.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">2. Pronunciation</h4>
                  <p className="text-sm text-[#83928c]">Clarity, phonetic accuracy, and accent adaptation. Evaluates how clearly your speech can be understood.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">3. Thoroughness</h4>
                  <p className="text-sm text-[#83928c]">Consistency and task completion with precision. Measures your ability to complete activities fully and accurately.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">4. Vocabulary</h4>
                  <p className="text-sm text-[#83928c]">Lexical range and word precision. Evaluates the diversity and appropriateness of words you use.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">5. Grammar</h4>
                  <p className="text-sm text-[#83928c]">Syntactic accuracy, tense consistency, and error frequency. Measures correctness of sentence structures.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">6. Writing Skills</h4>
                  <p className="text-sm text-[#83928c]">Coherence, creativity, and organization in written communication. Evaluates structured expression of ideas.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">7. Comprehension</h4>
                  <p className="text-sm text-[#83928c]">Understanding and inference of spoken and written material. Measures ability to grasp meaning and context.</p>
                </div>
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <h4 className="font-medium text-[#1a1a2e] mb-1">8. Attention to Detail</h4>
                  <p className="text-sm text-[#83928c]">Accuracy in capturing and reproducing specific information. Evaluates precision in following instructions.</p>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* How TPI Assessment Works */}
        <section className="section">
          <h2 className="heading-2 text-center mb-4">How TPI Assessment Works</h2>
          <p className="body-large text-center max-w-2xl mx-auto mb-12">
            Your TPI score is calculated through a combination of activity performance and final assessment.
          </p>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Activity-Based Assessment */}
              <div className="bg-white rounded-2xl p-6 border border-black/10">
                <div className="w-12 h-12 rounded-xl bg-[#3459a4]/10 flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-[#3459a4]" />
                </div>
                <h3 className="font-semibold text-lg text-[#1a1a2e] mb-3">Activity-Based Learning</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Practice 11 different module types</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">AI calculates Accuracy Score per activity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Reach 80% accuracy to unlock assessment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Pass module assessment with 80%+ score</span>
                  </li>
                </ul>
              </div>

              {/* TPI Grand Assessment */}
              <div className="bg-white rounded-2xl p-6 border border-black/10">
                <div className="w-12 h-12 rounded-xl bg-[#3459a4]/10 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-[#3459a4]" />
                </div>
                <h3 className="font-semibold text-lg text-[#1a1a2e] mb-3">TPI Grand Assessment</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Complete all 11 module assessments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Take comprehensive TPI Grand Assessment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Receive detailed score across 8 dimensions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3459a4] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#83928c]">Earn official Tenses Certificate</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Mastery Guarantee */}
            <div className="mt-8 p-6 bg-[#f97316]/5 rounded-xl border border-[#f97316]/20">
              <h4 className="font-medium text-[#1a1a2e] mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#f97316]" />
                Mastery Guarantee
              </h4>
              <p className="text-sm text-[#83928c]">
                If you fail a module assessment (score below 80%), your Accuracy Score resets to 0 and 
                you must practice again. This ensures your TPI certificate represents genuine English mastery, 
                not just completion of activities.
              </p>
            </div>
          </div>
        </section>

        {/* Why Get Certified */}
        <section className="section section-alt">
          <h2 className="heading-2 text-center mb-4">Why Get TPI Certified?</h2>
          <p className="body-large text-center max-w-2xl mx-auto mb-12">
            A Tenses Certificate opens doors to new opportunities in education and career.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: "Career Advancement",
                description: "Stand out in job applications with verified English proficiency that employers trust."
              },
              {
                title: "Clear Evidence",
                description: "A verifiable score and certificate link you can share with any employer."
              },
              {
                title: "Detailed Insights",
                description: "Understand your strengths and weaknesses across 8 skill dimensions."
              },
              {
                title: "Shareable Certificate",
                description: "Digital certificate that can be shared on LinkedIn and other platforms."
              },
              {
                title: "Continuous Improvement",
                description: "Retake assessments to track improvement and update your certification."
              },
              {
                title: "Trusted Assessment",
                description: "AI-powered evaluation ensures consistent and unbiased scoring."
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-black/5">
                <h3 className="font-semibold text-[#1a1a2e] mb-2">{benefit.title}</h3>
                <p className="text-sm text-[#83928c]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="section text-center">
          <h2 className="heading-2 mb-4">Ready to Get Certified?</h2>
          <p className="body-large max-w-xl mx-auto mb-8">
            Choose a plan today and begin your journey to TPI certification.
          </p>
          <a href="https://app.tenses.ai" className="btn-primary gap-2">
            Get started
            <ArrowRight className="w-4 h-4" />
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TPIPage;
